import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Trash2 } from 'lucide-react'
import { useShares } from '../hooks/useShares'
import { useSeeds } from '../hooks/useSeeds'
import { ShareCard } from '../components/ShareCard'
import { EmptyState } from '../components/EmptyState'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { BulkSelectToolbar } from '../components/BulkSelectToolbar'
import { LongRunningOpDialog, makeDeleteInterpreter } from '../components/LongRunningOpDialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Share } from '../types'

export function SharesPage() {
  const [seedFilter, setSeedFilter] = useState('')
  const [targetFilter, setTargetFilter] = useState('')
  const [toggleShare, setToggleShare] = useState<Share | null>(null)
  const [toggling, setToggling] = useState(false)
  const [deleteShare, setDeleteShare] = useState<Share | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const bulkInterpreter = useMemo(() => makeDeleteInterpreter(), [bulkOpen])

  function toggleSel(id: number) {
    setSelected(s => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const { shares, loading, error, updateShare, refetch } = useShares({
    seed: seedFilter || undefined,
    target: targetFilter || undefined,
  })
  const { seeds } = useSeeds()

  async function handleDeleteShare() {
    if (!deleteShare) return
    setDeleting(true)
    setDeleteError(null)
    try {
      const res = await fetch(`/api/shares/${deleteShare.id}`, { method: 'DELETE' })
      if (res.ok) {
        setDeleteShare(null)
        refetch()
      } else {
        const body = await res.json().catch(() => ({})) as { error?: string }
        setDeleteError(body.error || `Request failed (${res.status})`)
      }
    } catch (err) {
      setDeleteError(String(err))
    }
    setDeleting(false)
  }

  async function handleConfirmToggle(notify: boolean) {
    if (!toggleShare) return
    const newStatus = toggleShare.status === 'draft' ? 'published' : 'draft'
    try {
      setToggling(true)
      await updateShare(toggleShare.id, { status: newStatus })
      if (notify && newStatus === 'published') {
        try { await fetch(`/api/shares/${toggleShare.id}/notify`, { method: 'POST' }) } catch {}
      }
      setToggleShare(null)
      refetch()
    } catch {
      // updateShare already refetches on success
    } finally {
      setToggling(false)
    }
  }

  const expiredIds = shares.filter(s => s.expired).map(s => s.id)
  const [pruneExpiredOpen, setPruneExpiredOpen] = useState(false)
  const pruneExpiredInterpreter = useMemo(() => makeDeleteInterpreter(), [pruneExpiredOpen])

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Shares</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Give specific clients or domains access to a seed via named workspaces. Each workspace is its own isolated audience + discussion.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:text-amber-400 dark:border-amber-400/60 dark:hover:bg-amber-950/40"
            onClick={() => setPruneExpiredOpen(true)}
            disabled={expiredIds.length === 0}
            title={expiredIds.length === 0 ? 'No expired shares to prune' : `Prune ${expiredIds.length} expired share(s)`}
          >
            <Trash2 className="size-4 mr-1" />
            Prune expired ({expiredIds.length})
          </Button>
          <Button asChild>
            <Link to="/shares/new">
              <Plus />
              Create Share
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={seedFilter} onValueChange={(v) => setSeedFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All seeds" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All seeds</SelectItem>
            {seeds.map(s => (
              <SelectItem key={s.name} value={s.name}>{s.displayName}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="text"
          value={targetFilter}
          onChange={e => setTargetFilter(e.target.value)}
          placeholder="Search target..."
          className="w-[200px]"
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-4">
              <Skeleton className="h-4 w-1/3" />
            </Card>
          ))}
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error: {error}</AlertDescription>
        </Alert>
      ) : shares.length === 0 ? (
        <EmptyState
          title="No shares"
          description="Create a share to give clients access to a seed."
          action={{
            label: 'Create Share',
            onClick: () => { window.location.href = '/shares/new' },
          }}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead className="w-[60px]">ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Seed</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Workspaces</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[60px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shares.map(share => (
                <ShareCard
                  key={share.id}
                  share={share}
                  onStatusToggle={setToggleShare}
                  onDelete={setDeleteShare}
                  selected={selected.has(share.id)}
                  onToggleSelect={toggleSel}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <BulkSelectToolbar
        count={selected.size}
        itemLabel="share"
        onClear={() => setSelected(new Set())}
        onDelete={() => setBulkOpen(true)}
      />

      <LongRunningOpDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onComplete={() => { setSelected(new Set()); refetch() }}
        title={`Delete ${selected.size} share(s)`}
        description="For each share: wipe DOs across touched_versions, R2 prefix, D1 cascade (share_data, recipients, messages)."
        confirmLabel="Confirm delete"
        warning="This cannot be undone."
        request={{
          method: 'POST',
          url: '/api/shares/delete/bulk/stream',
          body: JSON.stringify({ ids: [...selected] }),
        }}
        interpretEvent={bulkInterpreter}
      />

      <LongRunningOpDialog
        open={pruneExpiredOpen}
        onClose={() => setPruneExpiredOpen(false)}
        onComplete={() => refetch()}
        title={`Prune ${expiredIds.length} expired share(s)`}
        description="For each expired share: wipe DOs across touched_versions, R2 prefix, D1 cascade."
        confirmLabel="Confirm prune"
        warning="Expired shares are permanently deleted. This cannot be undone."
        request={{
          method: 'POST',
          url: '/api/shares/delete/bulk/stream',
          body: JSON.stringify({ ids: expiredIds }),
        }}
        interpretEvent={pruneExpiredInterpreter}
      />


      {/* Status toggle confirmation modal */}
      <Dialog open={!!toggleShare} onOpenChange={open => { if (!open) setToggleShare(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {toggleShare?.status === 'draft' ? 'Publish share?' : 'Unpublish share?'}
            </DialogTitle>
          </DialogHeader>
          {toggleShare?.status === 'draft' ? (() => {
            const emails = toggleShare.workspaces.flatMap(w => w.members.filter(m => m.kind === 'email').map(m => m.recipient))
            return emails.length > 0 ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    This will make the share visible to recipients. Send email notification?
                  </p>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    {emails.map(e => <div key={e}>{e}</div>)}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" size="sm" disabled={toggling} onClick={() => handleConfirmToggle(false)}>
                      Publish without email
                    </Button>
                    <Button size="sm" disabled={toggling} onClick={() => handleConfirmToggle(true)}>
                      Publish &amp; notify
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    This will make the share visible to recipients.
                  </p>
                  <DialogFooter>
                    <Button variant="outline" size="sm" disabled={toggling} onClick={() => setToggleShare(null)}>Cancel</Button>
                    <Button size="sm" disabled={toggling} onClick={() => handleConfirmToggle(false)}>Publish</Button>
                  </DialogFooter>
                </>
              )
          })() : (
            <>
              <p className="text-sm text-muted-foreground">
                Clients will lose access to this share.
              </p>
              <DialogFooter>
                <Button variant="outline" size="sm" disabled={toggling} onClick={() => setToggleShare(null)}>Cancel</Button>
                <Button size="sm" variant="destructive" disabled={toggling} onClick={() => handleConfirmToggle(false)}>Unpublish</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete share confirmation modal */}
      <Dialog open={!!deleteShare} onOpenChange={open => { if (!open) { setDeleteShare(null); setDeleteError(null) } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete share?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently delete share <strong>#{deleteShare?.id}</strong>{deleteShare?.name ? ` (${deleteShare.name})` : ''}: all recipients, messages, and R2 storage. This action is irreversible.
          </p>
          {deleteError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" disabled={deleting} onClick={() => setDeleteShare(null)}>Cancel</Button>
            <Button size="sm" variant="destructive" disabled={deleting} onClick={handleDeleteShare}>
              {deleting ? 'Deleting...' : 'Delete share'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
